import { DOCUMENT } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  APP_ID,
  inject,
  importProvidersFrom,
  isDevMode,
  provideAppInitializer,
  signal,
  type EnvironmentProviders,
  type Provider,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatNativeDateModule } from '@angular/material/core';
import { Title } from '@angular/platform-browser';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { CoreEffects } from '@next/core-lib';
import { provideAgrAdvisorRelationshipsSelector } from '@next/core-lib/advisor-relationships-selector';
import { provideAgrCore } from '@next/core-lib/app';
import { provideAgrAppInsights } from '@next/core-lib/app-insights';
import { provideAgrAppInsightsBrowser } from '@next/core-lib/app-insights-browser';
import { BrandingService } from '@next/core-lib/branding';
import {
  I18nBrowserEffects,
  provideAgrBrowser,
  provideAgrWindowBrowser,
} from '@next/core-lib/browser';
import { provideAgrBusinessPartnerAggregationApi } from '@next/core-lib/business-partner-aggregation-api';
import { AgrNavigationRailModule, IgnoreStatusOfflinePatterns } from '@next/core-lib/components';
import { provideAgrCustomerDataApi } from '@next/core-lib/customer-data-api';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { EnvironmentInjector, provideAgrEnvironment } from '@next/core-lib/environment';
import {
  ENVIRONMENT_MAPPER,
  provideAgrEnvironmentBrowser,
} from '@next/core-lib/environment-browser';
import { AgrHeaderModule } from '@next/core-lib/header';
import {
  provideAgrI18nShared,
  I18nEffects,
  LocaleService,
  AgrCurrencyPipe,
  AgrDatePipe,
  AgrNumberPipe,
  AgrPercentPipe,
} from '@next/core-lib/i18n';
import { provideAgrNavigation, NavigationEffects } from '@next/core-lib/navigation';
import { PWAService, PWA_INSTALL_PROMPT_MODE } from '@next/core-lib/pwa';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { provideAgrSettings } from '@next/core-lib/settings';
import { provideAgrSite, SiteEffects } from '@next/core-lib/site';
import { ShipToIdentification, ShipToIdentifications } from '@next/core-lib/user';
import { provideAgrWindow } from '@next/core-lib/window';
import { provideAgrWrapperSiteSidenav } from '@next/core-lib/wrapper-site-sidenav';
import { provideEffects } from '@ngrx/effects';
import { provideStore, provideState } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';

import { appRoutes } from './app.routes';
import * as brandingReducer from './branding/store/branding.reducer';
import { provideCycleFeature } from './cycle/cycle.providers';
import { provideDeliveryFeature } from './delivery/delivery.providers';
import { provideFarmerInsightsFeature } from './farmer-insights/farmer-insights.providers';
import { provideFeedAdviceFeature } from './feed-advice/feed-advice.providers';
import { provideProductFeature } from './product/product.providers';
import { provideReferenceSchemeFeature } from './reference-scheme/reference-scheme.providers';
import { provideReferenceValueSheetFeature } from './reference-value-sheet/reference-value-sheet.providers';
import { provideReportFeature } from './report/report.providers';
import { provideSettingsFeature } from './settings/settings.providers';
import { FarmSettingsEffects } from './settings/store/effects/farm-settings.effects';
import { PoultryEffects } from './settings/store/effects/poultry.effects';
import { provideAppTable } from './shared/components/table/table.providers';
import { applyPwaBranding, getPwaAppName } from './shared/pwa/pwa-branding';
import { PwaBrandingService } from './shared/pwa/pwa-branding.service';
import { pwaRegistrationOptions } from './shared/pwa/pwa-worker';
import { loadRuntimeEnvironment } from './shared/pwa/runtime-environment';
import { provideTenantAwareTranslation } from './shared/services/tenant-aware-translation.provider';
import { provideSharedApp } from './shared/shared.providers';
import { provideWithinCyclePerformanceFeature } from './within-cycle-performance/within-cycle-performance.providers';

const loadedConfig = signal(environment);

declare global {
  var CONFIG_BUILD_ID: string | undefined;
}

export const setupRuntimeConfig = (async () => {
  const resolved = await loadRuntimeEnvironment(
    environment,
    window.location.hostname,
    globalThis.CONFIG_BUILD_ID,
  );
  loadedConfig.set(resolved);
  document.title = getPwaAppName(resolved.TENANT, resolved.DEFAULT_LOCALE);
  applyPwaBranding(document, resolved.TENANT, resolved.DEFAULT_LOCALE);
})();

export function provideAppBootstrap(): (EnvironmentProviders | Provider)[] {
  return [
    // Router setup - must come first
    provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
    // Core provider functions
    provideAgrBrowser({ enableServiceWorker: false }),
    provideAgrCore(),
    PWAService,
    // Feature providers
    provideAgrEnvironment(),
    provideAgrEnvironmentBrowser(),
    provideAgrI18nShared(),
    provideAgrSite(),
    provideAgrNavigation(),
    provideAgrWrapperSiteSidenav(),
    provideAgrWindow(),
    provideAgrWindowBrowser(),
    provideAgrAppInsights(),
    provideAgrAppInsightsBrowser(),
    provideAgrBusinessPartnerAggregationApi(),
    provideAgrCustomerDataApi(),
    provideAgrElasticApi(),
    provideAgrAdvisorRelationshipsSelector(),
    provideAgrSettings(),
    provideServiceWorker('sw-master.js', pwaRegistrationOptions()),
    { provide: PWA_INSTALL_PROMPT_MODE, useValue: 'native' },
    provideSharedApp(),
    provideAppTable(),
    provideCycleFeature(),
    provideDeliveryFeature(),
    provideProductFeature(),
    provideSettingsFeature(),
    provideReportFeature(),
    provideFarmerInsightsFeature(),
    provideWithinCyclePerformanceFeature(),
    provideFeedAdviceFeature(),
    provideReferenceValueSheetFeature(),
    provideReferenceSchemeFeature(),
    // HTTP client
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(AgrHeaderModule, AgrNavigationRailModule, MatNativeDateModule),
    // NgRx store setup
    provideStore(
      {},
      {
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
          strictStateSerializability: false,
          strictActionSerializability: false,
        },
      },
    ),
    provideState('brandingState', brandingReducer.reducer),
    provideEffects([
      PoultryEffects,
      FarmSettingsEffects,
      CoreEffects,
      I18nEffects,
      I18nBrowserEffects,
      SiteEffects,
      NavigationEffects,
    ]),
    // Router store - must come after router and store are set up
    provideAgrRouterStore(),
    // Store devtools
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode in production
      autoPause: true, // Pauses recording actions when extension window is not open
      trace: false, // Stack trace for actions
      traceLimit: 75, // Maximum stack trace frames
      connectInZone: true, // Connect in zone for better change detection tracking
    }),
    // Tenant awareness
    provideTenantAwareTranslation(),
    { provide: BrandingService, useClass: PwaBrandingService },
    { provide: Title, useExisting: BrandingService },
    provideAppInitializer(() => {
      const document = inject(DOCUMENT);
      const environment = inject(EnvironmentInjector);
      const branding = inject(BrandingService) as PwaBrandingService;
      inject(LocaleService)
        .locale$.pipe(takeUntilDestroyed())
        .subscribe((locale) => {
          applyPwaBranding(document, environment.TENANT, locale);
          branding.setTitle('');
        });
    }),
    // Injection tokens and pipes
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => loadedConfig(),
    },
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
    {
      provide: ShipToIdentifications,
      useValue: [ShipToIdentification.ExternalShipToId],
    },
    {
      provide: IgnoreStatusOfflinePatterns,
      useValue: [
        '/b2c_1_allaccountssignin/v2.0/.well-known/openid-configuration',
        '/b2c_1a_allaccountssignupsignin/v2.0/.well-known/openid-configuration',
        '/service/barn/*',
        '/service/business-partner-aggregation/*',
        '/service/navigation-v2/*',
        '/service/concept/*',
        '/service/hatchery/*',
        '/service/slaughterhouse/*',
        '/service/settings/*',
        '/service/notification/*',
        '/service/content/*',
        '/service/checkout/*',
        '/service/flock-registration/secure/observation/*',
        '/service/flock-registration/secure/cycle',
        '/service/poultry-feed-advice/secure/feedadvice/*',
        '/service/poultry-feed-advice/secure/product/*',
        '/service/poultry-feed-advice/secure/referenceValueSheet',
      ],
      multi: true,
    },
    AgrNumberPipe,
    AgrDatePipe,
    AgrCurrencyPipe,
    AgrPercentPipe,
  ];
}
